import React from 'react';
import ExpandableSection from 'src/shared/components/expandable-section/ExpandableSection';
import { habitatInsightsLandTypeColors, habitatInsightsPrimaryProductivityColors } from 'src/shared/constants/colors';
import { HabitatInsightsMetricGroup } from 'src/shared/types';
import styles from './HabitatMetricLegend.module.scss';

export type HabitatMetricLegendProps = {
    selectedMetricGroup: string;
};

const HabitatMetricLegend = ({ selectedMetricGroup }: HabitatMetricLegendProps) => {
    const commonProps = {
        isExpanded: true,
        classNames: {
            toggle: styles.toggleSection,
        },
    };
    return (
        <>
            {selectedMetricGroup === HabitatInsightsMetricGroup.LAND_COVER && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Land type</div>
                            <div>
                                {Object.values(habitatInsightsLandTypeColors).map((entry, index) => (
                                    <span className={styles.keyLabel} key={index}>
                                        <i style={{ background: entry.color }} className={styles.circle}></i>
                                        {entry.label}
                                    </span>
                                ))}
                            </div>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Land type</div>}
                />
            )}

            {selectedMetricGroup === 'COMBINED_VEGETATION_COVER' && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Combined vegetation cover</div>
                            <KeyColorScale metric={'COMBINED_VEGETATION_COVER'}>
                                <div>Highest</div>
                                <div>Lowest</div>
                            </KeyColorScale>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Combined vegetation cover</div>}
                />
            )}

            {selectedMetricGroup === HabitatInsightsMetricGroup.CONNECTIVITY && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Connectivity</div>
                            <KeyColorScale metric={HabitatInsightsMetricGroup.CONNECTIVITY}>
                                <div>Highest</div>
                                <div>Lowest</div>
                            </KeyColorScale>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Connectivity</div>}
                />
            )}

            {selectedMetricGroup === 'VEGETATION_HEALTH' && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Vegetation Health</div>
                            <KeyColorScale metric={'VEGETATION_HEALTH'}>
                                <div>Highest</div>
                                <div>Lowest</div>
                            </KeyColorScale>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Vegetation Health</div>}
                />
            )}

            {selectedMetricGroup === HabitatInsightsMetricGroup.PRIMARY_PRODUCTIVITY && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Vegetation Health</div>
                            <div>
                                {Object.values(habitatInsightsPrimaryProductivityColors).map((entry, index) => (
                                    <span className={styles.keyLabel} key={index}>
                                        <i style={{ background: entry.color }} className={styles.circle}></i>
                                        {entry.label}
                                    </span>
                                ))}
                            </div>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Vegetation Health</div>}
                />
            )}

            {selectedMetricGroup === HabitatInsightsMetricGroup.BIODIVERSITY_INTACTNESS_INDEX && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Biodiversity Intactness</div>
                            <KeyColorScale metric={HabitatInsightsMetricGroup.BIODIVERSITY_INTACTNESS_INDEX}>
                                <div>
                                    <span className='mr-2'>1.0</span>Intact
                                </div>
                                <div>
                                    <span className='mr-5'>&nbsp;</span>Moderate Intactness
                                </div>
                                <div>
                                    <span className='mr-5'>&nbsp;</span>Low Intactness
                                </div>
                                <div>
                                    <span className='mr-2'>0.0</span>Severely Degraded
                                </div>
                            </KeyColorScale>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Biodiversity intactness</div>}
                />
            )}

            {selectedMetricGroup === HabitatInsightsMetricGroup.HUMAN_MODIFICATION_INDEX && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Human Modification</div>
                            <KeyColorScale metric={HabitatInsightsMetricGroup.HUMAN_MODIFICATION_INDEX}>
                                <div>
                                    <span className='mr-2'>1.0</span>Very high modification
                                </div>
                                <div>
                                    <span className='mr-5'>&nbsp;</span>Moderate <br />
                                    <span className='mr-5'>&nbsp;</span>modification
                                </div>
                                <div>
                                    <span className='mr-2'>0.0</span>Natural
                                </div>
                            </KeyColorScale>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Human modification</div>}
                />
            )}

            {/* Portfolio special metrics */}
            {selectedMetricGroup === 'POSSIBLE_IUCN_RED_LIST_SPECIES' && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Predicted IUCN Red List species</div>
                            <div className='flex gap-3' data-testid='color-scale-wrapper'>
                                <div className={`${styles.gradientScale} ${styles.redListGradient}`}>&nbsp;</div>
                                <div className='flex flex-col justify-between'>
                                    <div>Highest number of species</div>
                                    <div>Lowest number of species</div>
                                </div>
                            </div>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Predicted IUCN Red List species</div>}
                />
            )}

            {selectedMetricGroup === 'POSSIBLE_INVASIVE_SPECIES' && (
                <ExpandableSection
                    {...commonProps}
                    expandedContent={
                        <>
                            <div className={styles.keyTitle}>Predicted invasive species</div>
                            <div className='flex gap-3' data-testid='color-scale-wrapper'>
                                <div className={`${styles.gradientScale} ${styles.invasiveGradient}`}>&nbsp;</div>
                                <div className='flex flex-col justify-between'>
                                    <div>Highest number of species</div>
                                    <div>Lowest number of species</div>
                                </div>
                            </div>
                        </>
                    }
                    collapsedContent={<div className={styles.keyTitle}>Predicted invasive species</div>}
                />
            )}
        </>
    );
};

type KeyColorScaleProps = {
    children: React.ReactNode;
    metric:
        | HabitatInsightsMetricGroup.CONNECTIVITY
        | HabitatInsightsMetricGroup.BIODIVERSITY_INTACTNESS_INDEX
        | HabitatInsightsMetricGroup.HUMAN_MODIFICATION_INDEX
        | 'COMBINED_VEGETATION_COVER'
        | 'VEGETATION_HEALTH';
};
export const KeyColorScale = (props: KeyColorScaleProps) => {
    const { metric, children } = props;
    const gradientClasses = `${styles.gradientScale} ${
        [HabitatInsightsMetricGroup.CONNECTIVITY, 'COMBINED_VEGETATION_COVER', 'VEGETATION_HEALTH'].includes(metric)
            ? styles.connectivityGradient
            : metric === HabitatInsightsMetricGroup.BIODIVERSITY_INTACTNESS_INDEX
            ? styles.biiGradient
            : styles.hmiGradient
    }`;
    return (
        <div className='flex gap-3' data-testid='color-scale-wrapper'>
            <div className={gradientClasses}>&nbsp;</div>
            <div className='flex flex-col justify-between'>{children}</div>
        </div>
    );
};

export default HabitatMetricLegend;
